/**
 * Browser storage utilities for persisting application data
 */

/**
 * Get a value from localStorage with fallback
 * @param key - The key to retrieve from localStorage
 * @param fallback - Fallback value if key doesn't exist or localStorage is unavailable
 * @returns The stored value or fallback
 */
export function getStorageItem<T>(key: string, fallback: T): T {
  // Check if localStorage is available (browser environment)
  if (typeof window === "undefined" || !window.localStorage) {
    return fallback;
  }

  try {
    const item = window.localStorage.getItem(key);
    if (item === null) {
      return fallback;
    }
    return JSON.parse(item);
  } catch (error) {
    // If JSON parsing fails or localStorage throws an error, return fallback
    console.warn(`Failed to get storage item "${key}":`, error);
    return fallback;
  }
}

/**
 * Set a value in localStorage
 * @param key - The key to store in localStorage
 * @param value - The value to store (will be JSON stringified)
 * @returns boolean indicating success
 */
export function setStorageItem<T>(key: string, value: T): boolean {
  // Check if localStorage is available (browser environment)
  if (typeof window === "undefined" || !window.localStorage) {
    return false;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    // localStorage might be full or disabled
    console.warn(`Failed to set storage item "${key}":`, error);
    return false;
  }
}

/**
 * Remove a value from localStorage
 * @param key - The key to remove from localStorage
 * @returns boolean indicating success
 */
export function removeStorageItem(key: string): boolean {
  // Check if localStorage is available (browser environment)
  if (typeof window === "undefined" || !window.localStorage) {
    return false;
  }

  try {
    window.localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`Failed to remove storage item "${key}":`, error);
    return false;
  }
}
